'use client'

import { useState } from 'react'
import { useInView } from '@/hooks/useInView'
import { X, CheckCircle } from 'lucide-react'

/* ── Pain points vs benefits ─────────────────────────────── */

const OLD_WAY = [
  '8+ disconnected apps',
  'Manual follow-ups',
  'Spreadsheet estimates',
  'Lost leads everywhere',
  'No real-time visibility',
  'Hours of admin work daily',
]

const NEW_WAY = [
  'One connected platform',
  'AI-powered follow-ups 24/7',
  'Satellite-powered estimates',
  'Every lead tracked and nurtured',
  'Real-time dashboard',
  'Minutes, not hours',
]

/* ── Delay class lookup for staggered children ───────────── */
const DELAY_CLASSES = [
  'anim-delay-1',
  'anim-delay-2',
  'anim-delay-3',
  'anim-delay-4',
  'anim-delay-5',
  'anim-delay-6',
]

/* ── Single Flip Card ────────────────────────────────────── */

function FlipCard({
  pain,
  solution,
  index,
  isInView,
}: {
  pain: string
  solution: string
  index: number
  isInView: boolean
}) {
  const [isFlipped, setIsFlipped] = useState(false)

  return (
    <div
      className={`anim-fade-in-up ${DELAY_CLASSES[index] ?? ''} ${isInView ? 'in-view' : ''} relative h-52 sm:h-56 cursor-pointer group`}
      style={{ perspective: '1000px' }}
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
      onTouchStart={() => setIsFlipped((f) => !f)}
      role="button"
      tabIndex={0}
      aria-label={`Pain: ${pain}. Solution: ${solution}. ${isFlipped ? 'Showing solution' : 'Hover or tap to see solution'}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setIsFlipped((f) => !f)
        }
      }}
    >
      <div
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        className="relative w-full h-full"
      >
        {/* ── Front (pain point) ──────────────────────────── */}
        <div
          className="absolute inset-0 glass-card p-6 flex flex-col justify-between bg-gradient-to-br from-red-950/30 via-red-950/10 to-transparent border-red-900/20"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div>
            <div className="w-10 h-10 rounded-xl bg-red-600/10 flex items-center justify-center mb-4">
              <X size={20} className="text-red-500" />
            </div>
            <p className="text-sm sm:text-base text-zinc-300 font-medium leading-relaxed">
              {pain}
            </p>
          </div>
          <p className="text-[11px] text-zinc-600 flex items-center gap-1.5 mt-3">
            <span className="inline-block w-4 h-px bg-zinc-700" />
            <span className="hidden md:inline">Hover to flip</span>
            <span className="md:hidden">Tap to flip</span>
          </p>
        </div>

        {/* ── Back (solution) ─────────────────────────────── */}
        <div
          className="absolute inset-0 glass-card p-6 flex flex-col justify-between bg-gradient-to-br from-emerald-950/30 via-emerald-950/10 to-transparent border-emerald-900/20"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          <div>
            <div className="w-10 h-10 rounded-xl bg-emerald-600/10 flex items-center justify-center mb-4">
              <CheckCircle size={20} className="text-emerald-500" />
            </div>
            <p className="text-sm sm:text-base text-zinc-200 font-medium leading-relaxed">
              {solution}
            </p>
          </div>
          <p className="text-[11px] text-emerald-600/60 flex items-center gap-1.5 mt-3">
            <span className="inline-block w-4 h-px bg-emerald-800/40" />
            BuilderLync
          </p>
        </div>
      </div>
    </div>
  )
}

/* ── Component (same export name) ────────────────────────── */

export function BeforeAfterSlider() {
  const { ref, isInView } = useInView()
  const viewCls = isInView ? 'in-view' : ''

  return (
    <section className="section" ref={ref}>
      <div className="section-inner">
        {/* Section Header */}
        <div className={`anim-fade-in-up ${viewCls} text-center mb-10 sm:mb-14`}>
          <p className="text-xs uppercase tracking-[0.2em] text-red-500 font-semibold mb-3">
            The Difference
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight leading-tight max-w-2xl mx-auto">
            See the{' '}
            <span className="gradient-text-red">Difference.</span>
          </h2>
          <p className="text-zinc-400 mt-4 max-w-lg mx-auto text-sm sm:text-base">
            Tap or hover each card to see how BuilderLync replaces the old way.
          </p>
        </div>

        {/* Flip Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
          {OLD_WAY.map((pain, i) => (
            <FlipCard
              key={pain}
              pain={pain}
              solution={NEW_WAY[i]}
              index={i}
              isInView={isInView}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
